import { useState, useCallback, useRef, useEffect, useMemo } from 'react';

import {
  FilterValues,
  SortConfig,
  PaginationInfo,
} from '@/shared/components/DataTable';

import {
  CustomersManagerState,
  GetCustomersParams,
} from '../models/customer.model';
import { CLIENT_SIDE_SORTABLE_FIELDS, ConnectionStatus } from '../constants';

import { useCustomerMutations } from '../queries/customers.query';
import { useUsageMutations } from '@/shared/queries/customers.query';
import { useCustomerService } from '../services/customers.service';

import { sortCustomers } from '../utils/sortUtils';


export const useCustomersManager = (
  initialFilters: FilterValues = {},
  tenantId?: string | null
) => {
  // Parse initial state from URL query params so filters/pagination/sort
  // are preserved on refresh.
  const parseInitialState = (): CustomersManagerState => {

      const params = new URLSearchParams(window.location.search);
      const page = Number(params.get('page') ?? '1') || 1;
      const limit = Number(params.get('limit') ?? '10') || 10;
      const sortField = params.get('sortBy') ?? undefined;
      const sortDirection =
        (params.get('sortOrder') as 'asc' | 'desc' | null) ?? undefined;
  
      const filters: FilterValues = { ...initialFilters };
      // map known filter keys used by the table
      const nameOrEmail = params.get('nameOrEmailSearch');
      if (nameOrEmail) filters.nameOrEmailSearch = nameOrEmail;
      const city = params.get('citySearch');
      if (city) filters.citySearch = city;
      const zip = params.get('zipCodeSearch');
      if (zip) filters.zipCodeSearch = zip;
      const meterStatus = params.get('meterStatus');
      if (meterStatus) filters.meterStatus = meterStatus;
      const connectionStatus = params.get('connectionStatus');
      if (connectionStatus === ConnectionStatus.DISCONNECTED) {
        filters.connectionStatus = ConnectionStatus.DISCONNECTED;
      }
  
      let status = params.get('status');
      if (!status) {
        status = 'active';
        params.set('status', status);
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        requestAnimationFrame(() => {
          window.history.replaceState({}, '', newUrl);
        });
      }
  
      filters.status = status;
  
      return {
        page,
        limit,
        sortField,
        sortDirection,
        filters,
      };
  };
  

  // Consolidated state for all request parameters
  const [requestState, setRequestState] =
    useState<CustomersManagerState>(parseInitialState);
  const previousTenantIdRef = useRef<string | null | undefined>(tenantId);

  const service = useCustomerService();
  const {
    useListCustomersQuery,
    useGetCustomerByIdQuery,
    updateCustomer,
    cancelSubscription,
  } = useCustomerMutations();

const {useGetCustomerDailyUsageQuery} = useUsageMutations()


  const isClientSideSort = CLIENT_SIDE_SORTABLE_FIELDS.includes(
    requestState.sortField || ''
  );

  const queryParams: GetCustomersParams = {
    page: requestState.page,
    limit: requestState.limit,
    sortBy: isClientSideSort ? undefined : requestState.sortField,
    sortOrder: isClientSideSort ? undefined : requestState.sortDirection,
    zipCodeSearch: requestState.filters?.zipCodeSearch as string,
    nameOrEmailSearch: requestState.filters?.nameOrEmailSearch as string,
    citySearch: requestState.filters?.citySearch as string,
    meterStatus: requestState.filters?.meterStatus as string,
    status: requestState.filters?.status as string,
    ...(requestState.filters?.connectionStatus === ConnectionStatus.DISCONNECTED
      ? { connectionStatus: ConnectionStatus.DISCONNECTED }
      : {}),
    ...(tenantId ? { tenantId } : {}),
  };

  // Reset to first page whenever tenant scope changes from the toolbar.
  useEffect(() => {
    if (previousTenantIdRef.current === tenantId) return;
    previousTenantIdRef.current = tenantId;
    setRequestState(prev =>
      prev.page === 1 ? prev : { ...prev, page: 1 }
    );
  }, [tenantId]);

  // Fetch customers
  const {
    data: customersData,
    isLoading,
    error,
  } = useListCustomersQuery(queryParams, {
    enabled: true,
  });

  
// Client-side sorting
const customers = useMemo(() => {
  if (!customersData?.data) return [];
  if (!requestState.sortField || !requestState.sortDirection)
    return customersData.data;

  if (isClientSideSort) {
    return sortCustomers(///Currently client side sorting  is disabled for (EnergyBalance , SubscriptionStatus ,SubscriptionEndDate, MeterStatus) if needed  change the  sortable: true in CustomerDataTable,
      customersData.data,
      requestState.sortField,
      requestState.sortDirection
    );
  }

  return customersData.data;
}, [
  customersData?.data,
  requestState.sortField,
  requestState.sortDirection,
  isClientSideSort,
]);

  const pagination: PaginationInfo = customersData?.Page
    ? {
        page: customersData.Page,
        pageSize: customersData.Limit,
        total: customersData.TotalCount,
        totalPages: customersData.TotalPageCount,
      }
    : {
        page: requestState.page,
        pageSize: requestState.limit,
        total: 0,
        totalPages: 0,
      };

  // Click Handlers
  const handlePageChange = useCallback((page: number) => {
    setRequestState(prev => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setRequestState(prev => ({ ...prev, page: 1, limit: pageSize }));
  }, []);

  const handleSortChange = useCallback((sort: SortConfig | null) => {
    setRequestState(prev => {
      if (!sort?.field) {
        return {
          ...prev,
          sortField: undefined,
          sortDirection: undefined,
          page: 1,
        };
      }

      const field = sort.field;
      const direction = sort.direction;

      if (prev.sortField === field) {
        if (prev.sortDirection === 'asc')
          return { ...prev, sortField: field, sortDirection: 'desc', page: 1 };
        if (prev.sortDirection === 'desc')
          return {
            ...prev,
            sortField: undefined,
            sortDirection: undefined,
            page: 1,
          };
      }

      return { ...prev, sortField: field, sortDirection: direction, page: 1 };
    });
  }, []);

  const handleFilterChange = useCallback((filters: FilterValues) => {
    setRequestState(prev => {
      // Avoid resetting the page if the filters didn't actually change.
      const prevFilters = prev.filters ?? {};
      const prevJson = JSON.stringify(prevFilters);
      const newJson = JSON.stringify(filters ?? {});
      if (prevJson === newJson) {
        return prev; // no change
      }

      return { ...prev, page: 1, filters };
    });
  }, []);

  // Sync request state to URL whenever filters/pagination/sort change.
  // We skip the first render because parseInitialState already reflected URL.
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }

    const params = new URLSearchParams();
    // pagination
    if (requestState.page && requestState.page > 1)
      params.set('page', String(requestState.page));
    if (requestState.limit && requestState.limit !== 10)
      params.set('limit', String(requestState.limit));
    // sort
    if (requestState.sortField)
      params.set('sortBy', String(requestState.sortField));
    if (requestState.sortDirection)
      params.set('sortOrder', String(requestState.sortDirection));
    // filters
    if (requestState.filters?.nameOrEmailSearch)
      params.set(
        'nameOrEmailSearch',
        String(requestState.filters.nameOrEmailSearch)
      );
    if (requestState.filters?.citySearch)
      params.set('citySearch', String(requestState.filters.citySearch));
    if (requestState.filters?.zipCodeSearch)
      params.set('zipCodeSearch', String(requestState.filters.zipCodeSearch));
    if (requestState.filters?.meterStatus)
      params.set('meterStatus', String(requestState.filters.meterStatus));
    if (requestState.filters?.status)
      params.set('status', String(requestState.filters.status));
    if (requestState.filters?.connectionStatus === ConnectionStatus.DISCONNECTED) {
      params.set('connectionStatus', ConnectionStatus.DISCONNECTED);
    }

    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [
    requestState.page,
    requestState.limit,
    requestState.sortField,
    requestState.sortDirection,
    requestState.filters,
  ]);

  return {
    customers,
    pagination,
    loading: isLoading,
    error,
    currentFilters: requestState.filters,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleFilterChange,
    // Expose single-customer helpers from the underlying mutations/queries
    useGetCustomerByIdQuery,
    updateCustomer,
    cancelSubscription,
    useGetCustomerDailyUsageQuery,
    // telemetry
    useGetCustomerTelemetryQuery:
      useCustomerMutations().useGetCustomerTelemetryQuery,
    // Programmatic fetch helper for single customer (returns a Promise)
    getCustomer: useCallback(
      (id: string) => service.getCustomerById(id),
      [service]
    ),
  };
};



